/**
 * Widget Registry - verwaltet die Registrierung aller Widget-Typen
 */

import logger from "../logging/logger";

class WidgetRegistry {
  constructor() {
    this.widgets = new Map();
    this.instances = new Map();
  }

  /**
   * Registriert einen neuen Widget-Typ
   * @param widgetDefinition Die Definition des Widget-Typs
   */
  registerWidget(widgetDefinition) {
    if (this.widgets.has(widgetDefinition.type)) {
      logger.warn(`Widget type '${widgetDefinition.type}' is already registered.`);
      return;
    }
    
    // Standardgröße hinzufügen, falls nicht vorhanden
    if (!widgetDefinition.defaultConfig.size) {
      widgetDefinition.defaultConfig.size = { width: 1, height: 1 };
    }
    
    this.widgets.set(widgetDefinition.type, widgetDefinition);
    logger.log(`Widget type '${widgetDefinition.type}' registered successfully.`);
  }

  /**
   * Gibt einen registrierten Widget-Typ zurück
   * @param type Der Typ des Widgets
   * @returns Die Widget-Definition oder undefined
   */
  getWidget(type) {
    return this.widgets.get(type);
  }

  /**
   * Gibt alle registrierten Widget-Typen zurück
   * @returns Array aller registrierten Widget-Definitionen
   */
  getAllWidgets() {
    return Array.from(this.widgets.values());
  }

  /**
   * Prüft, ob ein Widget-Typ registriert ist
   * @param type Der Typ des Widgets
   * @returns true, wenn der Widget-Typ registriert ist
   */
  isRegistered(type) {
    return this.widgets.has(type);
  }

  /**
   * Erstellt eine neue Widget-Instanz mit Standardkonfiguration
   * @param type Der Typ des Widgets
   * @param id Die eindeutige ID der Instanz
   * @returns Die neue Widget-Konfiguration oder null bei Fehler
   */
  createInstance(type, id) {
    const widgetDefinition = this.getWidget(type);
    
    if (!widgetDefinition) {
      logger.error(`Cannot create instance: Widget type '${type}' is not registered.`);
      return null;
    }
    
    const config = {
      id,
      type,
      title: widgetDefinition.defaultConfig.title || widgetDefinition.name,
      isActive: true,
      position: 0,
      globalSettings: widgetDefinition.defaultConfig.globalSettings || {},
      ...(widgetDefinition.defaultConfig.localSettings && {
        localSettings: widgetDefinition.defaultConfig.localSettings
      })
    };
    
    this.instances.set(id, config);
    return config;
  }

  /**
   * Gibt eine Widget-Instanz zurück
   * @param id Die ID der Instanz
   * @returns Die Widget-Konfiguration oder undefined
   */
  getInstance(id) {
    return this.instances.get(id);
  }

  /**
   * Aktualisiert eine Widget-Instanz
   * @param id Die ID der Instanz
   * @param config Die aktualisierte Konfiguration
   */
  updateInstance(id, config) {
    if (!this.instances.has(id)) {
      logger.warn(`Cannot update instance: Widget with id '${id}' does not exist.`);
      return;
    }
    
    this.instances.set(id, config);
  }

  /**
   * Löscht eine Widget-Instanz
   * @param id Die ID der Instanz
   */
  removeInstance(id) {
    this.instances.delete(id);
  }

  /**
   * Gibt alle Widget-Instanzen zurück
   * @returns Array aller Widget-Konfigurationen
   */
  getAllInstances() {
    return Array.from(this.instances.values());
  }
}

// Export einer singleton Instanz der WidgetRegistry
export const widgetRegistry = new WidgetRegistry();
